<template>
  <div class="tw:bg-white tw:w-full tw:border-b tw:border-gray-200">
    <div
      class="tw:flex tw:justify-between tw:items-center tw:mx-auto"
      :class="
        isLanding
          ? 'tw:lg:max-w-(--tw-breakpoint-landing) tw:md:max-w-(--tw-breakpoint-md) tw:sm:px-8 tw:px-4 tw:lg:px-0 tw:py-1 tw:lg:py-0'
          : 'tw:w-full tw:px-5'
      "
    >
      <div class="tw:duration-100 tw:relative tw:w-auto tw:shrink-0">
        <router-link v-if="pageName === 'studio-plus'" to="/getstarted"
          ><img :src="BusinessPlusLogo" alt="Business Studio+" class="tw:h-[18px] tw:xs:h-[22px]"
        /></router-link>
        <router-link v-else-if="pageName === 'rate-connect'" to="/getstarted"
          ><img :src="RateConnectLogo" alt="Rate Connect" class="tw:h-[18px] tw:xs:h-[22px]"
        /></router-link>
        <Logo :isLanding="true" v-else />
      </div>
      <nav class="navigation tw:h-16 tw:lg:flex tw:justify-center tw:py-auto tw:hidden tw:mx-auto tw:pl-2">
        <BaseLink
          class="tw:flex tw:items-center tw:text-sm tw:pr-[24px] tw:hover:cursor-pointer"
          :to="{ name: 'getstarted' }"
          data-testid="header-home-link"
        >
          {{ $t('navigation.home') }}
        </BaseLink>
        <div
          class="dropdown-menu tw:flex tw:relative tw:items-center tw:w-fit tw:pr-[24px] tw:text-sm tw:hover:cursor-pointer"
          data-testid="header-products-dropdown"
        >
          {{ $t('navigation_menu_our_solutions') }}
          <ChevronRight
            class="tw:absolute tw:hover:fill-blue-800 arrow-dropdown tw:fill-gray-800 tw:w-[18px] tw:h-[14px]"
          />
          <WebMenu type="products" :links="productLinks" class="tw:hidden menu-box" />
        </div>
        <div
          class="dropdown-menu tw:flex tw:relative tw:items-center tw:w-fit tw:text-sm tw:pr-[24px] tw:ml-2 tw:hover:cursor-pointer"
          data-testid="header-support-center-dropdown"
        >
          {{ $t('navigation_menu_support_center') }}
          <ChevronRight
            class="tw:absolute tw:hover:fill-blue-800 arrow-dropdown tw:fill-gray-800 tw:w-[18px] tw:h-[14px]"
          />
          <WebMenu type="support" :links="supportLinks" class="tw:hidden menu-box" />
        </div>
      </nav>
      <nav>
        <ul class="tw:w-full tw:flex tw:items-center tw:justify-end tw:gap-2.5 tw:md:gap-1">
          <li>
            <LanguageSelector size="big" class="tw:md:block tw:hidden" version="footer" isLanding />
            <div class="tw:md:hidden tw:flex tw:px-1" @click="toggleLanguageMenu" @keydown.enter="toggleLanguageMenu">
              <LanguageIcon class="tw:h-[24px] tw:w-[24px]" />
            </div>
          </li>
          <li>
            <div class="tw:md:hidden tw:flex tw:px-1 tw:mx-0" @click="onRegisterClick" @keydown.enter="onRegisterClick">
              <UserIcon class="tw:h-[24px] tw:w-[24px]" />
            </div>
            <MainButton
              size="medium"
              no-padding
              class="tw:hidden tw:md:flex tw:whitespace-nowrap tw:p-0"
              @click="onRegisterClick"
              @keydown.enter="onRegisterClick"
              :dataTestId="'header-register-button'"
            >
              {{ $t('tbs_mp_log_in_cta') }}
            </MainButton>
          </li>
          <li class="tw:lg:hidden tw:flex tw:items-center tw:sm:ml-4">
            <div
              class="nav-icon tw:h-[24px] tw:w-[30px] tw:relative tw:my-[17px] tw:mx-auto tw:cursor-pointer"
              :class="{ 'nav-icon-open': isMenuOpen }"
              @click="() => toggleMenu(true)"
              @keydown.enter="() => toggleMenu(true)"
            >
              <span></span>
              <span></span>
              <span></span>
            </div>
            <span class="tw:hidden tw:md:inline-flex tw:ml-4">{{ 'Menu' }}</span>
          </li>
        </ul>
      </nav>
    </div>
    <LanguageMobileMenu
      class="tw:flex tw:items-center tw:w-full"
      :isMenuOpen="isLanguageMenuOpen"
      @onClickaway="onLanguageMenuClickaway"
    />
    <MobileMenu
      :supportLinks="supportLinks"
      :productLinks="productLinks"
      class="tw:flex tw:items-center tw:w-full"
      :isMenuOpen="isMenuOpen"
      @onClickaway="onClickaway"
    />
  </div>
</template>

<script setup>
import { ref } from 'vue'

import UserIcon from '@/components/icons/UserIcon.svg?component'
import Logo from '@/components/dashboard/Logo.vue'
import MainButton from '@/components/base/buttons/MainButton.vue'
import LanguageSelector from '@/components/locale/LanguageSelector.vue'
import ChevronRight from '@/components/icons/ChevronRight.svg?component'
import MobileMenu from '@/components/landingPage/MobileMenu.vue'
import WebMenu from '@/components/landingPage/WebMenu.vue'
import BaseLink from '@/components/BaseLink.vue'
import appAnalytics from '@/utils/tracking'
import LanguageIcon from '@/components/icons/LanguageIcon.svg?component'
import LanguageMobileMenu from './LanguageMobileMenu.vue'

const BusinessPlusLogo = new URL('/src/assets/img/landing/landing_studio_plus.svg', import.meta.url)
const RateConnectLogo = new URL('/src/assets/img/landing/landing_rate_connect.svg', import.meta.url)

defineProps({
  pageName: String,
  isLanding: {
    type: Boolean,
    default: false,
  },
})
const isLanguageMenuOpen = ref(false)
const isMenuOpen = ref(false)
const isMenuMobile = ref(false)
const productLinks = [
  {
    id: 0,
    newPage: false,
    label: 'navigation_menu_business_studio_plus',
    url: '/home/getstarted/studio-plus',
    dataTestId: 'menu-header-studio-plus-link',
    withIcon: false,
    isUrl: true,
  },
  {
    id: 1,
    newPage: false,
    label: 'navigation_menu_rate_connect',
    url: '/home/getstarted/rate-connect',
    dataTestId: 'menu-header-rate-connect-link',
    withIcon: false,
    isUrl: true,
  },
]
const supportLinks = ref([
  {
    id: 0,
    newPage: true,
    label: 'navigation_menu_support_1',
    url: 'contactUs',
    dataTestId: 'header-contact-us-link',
    withIcon: true,
    isUrl: false,
  },
  {
    id: 1,
    newPage: true,
    label: 'navigation_menu_support_2',
    url: 'supportCentre',
    dataTestId: 'header-support-center-link',
    withIcon: true,
    isUrl: false,
  },
  {
    id: 2,
    newPage: true,
    label: 'navigation_menu_resources',
    url: 'https://www.businessblog-trivago.com/',
    dataTestId: 'header-trivago-blog-link',
    withIcon: true,
    isUrl: true,
  },
])

const toggleMenu = isMobile => {
  isMenuOpen.value = !isMenuOpen.value
  isMenuMobile.value = isMobile
}
const onClickaway = () => {
  if (isMenuOpen.value) {
    isMenuOpen.value = false
  }
}

const toggleLanguageMenu = () => {
  isLanguageMenuOpen.value = !isLanguageMenuOpen.value
}

const onLanguageMenuClickaway = () => {
  if (isLanguageMenuOpen.value) {
    isLanguageMenuOpen.value = false
  }
}

const emit = defineEmits(['onRegisterClick'])
const onRegisterClick = e => {
  appAnalytics.track(appAnalytics.events.LP_LOGIN_REGISTER_BUTTON_CLICKED)
  emit('onRegisterClick', e.target.value)
}
</script>
<style scoped>
.navigation div:hover .menu-box,
.menu-box:hover {
  display: flex;
}
.navigation div:hover svg:not(.external-icon) {
  fill: var(--tw-color-blue-800);
  transform: translateY(-1px) translateX(-8px) rotate(270deg);
}
.arrow-dropdown {
  top: 1px;
  bottom: 0;
  height: 100%;
  right: 0;
  pointer-events: none;
  transform: translateY(0%) rotate(90deg);
}
.nav-icon {
  transform: rotate(0deg);
  transition: 0.5s ease-in-out;
}
.nav-icon span {
  display: block;
  position: absolute;
  height: 2px;
  width: 100%;
  opacity: 1;
  left: 0;
  border-radius: var(--radius-lg);
  background: #37454d;
  transform: rotate(0deg);
  transition: 0.25s ease-in-out;
}

.nav-icon span:nth-child(1) {
  top: 0px;
  transform-origin: left center;
}

.nav-icon span:nth-child(2) {
  top: 10px;
  transform-origin: left center;
}

.nav-icon span:nth-child(3) {
  top: 20px;
  transform-origin: left center;
}

.nav-icon-open span:nth-child(1) {
  transform: rotate(45deg);
  top: 2px;
  left: 0;
}

.nav-icon-open span:nth-child(2) {
  width: 0%;
  opacity: 0;
}

.nav-icon-open span:nth-child(3) {
  transform: rotate(-45deg);
  top: 23px;
  left: 0;
}

.sign-button {
  @media (max-width: 540px) {
    display: none;
  }
}
</style>
